import * as DialogPrimitive from "@radix-ui/react-dialog"
import { AnimatePresence, motion } from "motion/react"
import {
	createContext,
	useCallback,
	useState,
	type ComponentProps
} from "react"
import { cn } from "~/lib/utils"

// Undefined uses the default body portal; null waits for the dialog's DOM ref.
export const DialogContentContainerContext = createContext<
	HTMLElement | null | undefined
>(undefined)

function Dialog({ ...props }: ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
	...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
	...props
}: ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
	...props
}: ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
	className,
	...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn("fixed inset-0 z-40 bg-black/80", className)}
			{...props}
		/>
	)
}

function DialogContent({
	className,
	children,
	size = "default",
	layout = "default",
	onInteractOutside,
	ref,
	...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
	size?: "default" | "md" | "lg" | "xl" | "full"
	layout?: "default" | "scroll" | "settings"
}) {
	const [container, setContainer] = useState<HTMLDivElement | null>(null)
	const contentRef = useCallback(
		(node: HTMLDivElement | null) => {
			setContainer(node)
			if (typeof ref === "function") return ref(node)
			if (ref) ref.current = node
		},
		[ref]
	)
	return (
		<DialogPortal data-slot="dialog-portal">
			<AnimatePresence mode="wait">
				<motion.div
					key="dialog"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15 }}
					className="fixed inset-0 z-50 flex items-center justify-center"
				>
					<DialogOverlay />
					{/* asChild must receive the actual DOM child, not the context provider. */}
					<DialogContentContainerContext.Provider value={container}>
						<DialogPrimitive.Content
							ref={contentRef}
							data-slot="dialog-content"
							onInteractOutside={(event) => {
								// Toast actions must not dismiss the dialog before their click runs.
								if (
									event.target instanceof Element &&
									event.target.closest("[data-sonner-toast]")
								) {
									event.preventDefault()
								}
								onInteractOutside?.(event)
							}}
							className={cn(
								"bg-background relative z-50 grid w-full max-w-[calc(100%-1rem)] max-h-[calc(100%-2rem)] gap-4 rounded-xl surface-floating has-[[cmdk-root]]:p-0",
								size === "default" && "sm:max-w-lg",
								size === "md" && "sm:max-w-2xl",
								size === "lg" && "sm:max-w-4xl",
								size === "xl" && "sm:max-w-5xl",
								size === "full" && "sm:max-w-6xl",
								layout === "default" && "p-6",
								layout === "scroll" &&
									"grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden p-0",
								layout === "settings" &&
									"h-[min(880px,calc(100svh-2rem))] overflow-hidden p-0 gap-0",
								className
							)}
							{...props}
						>
							{children}
						</DialogPrimitive.Content>
					</DialogContentContainerContext.Provider>
				</motion.div>
			</AnimatePresence>
		</DialogPortal>
	)
}

function DialogHeader({
	className,
	variant = "default",
	...props
}: ComponentProps<"div"> & { variant?: "default" | "divided" }) {
	return (
		<div
			data-slot="dialog-header"
			className={cn(
				"flex flex-col gap-2 text-center sm:text-left",
				variant === "divided" &&
					"m-0 w-full border-b border-stroke px-4 py-4 sm:px-7",
				className
			)}
			{...props}
		/>
	)
}

function DialogFooter({
	className,
	children,
	variant = "default",
	...props
}: ComponentProps<"div"> & { variant?: "default" | "divided" }) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				"flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center",
				variant === "divided" && "border-t border-stroke px-4 py-3 sm:px-6",
				className
			)}
			{...props}
		>
			{children}
		</div>
	)
}

function DialogTitle({
	className,
	...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn(
				"text-lg leading-none font-semibold tracking-tight",
				className
			)}
			{...props}
		/>
	)
}

function DialogDescription({
	className,
	...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn("text-muted-foreground text-sm", className)}
			{...props}
		/>
	)
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger
}
